import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  CardHeader,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledButtonDropdown,
} from "reactstrap";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { sentenceCase, titleCase } from "../../utils/case";

import {
  getReservations,
  deleteReservation,
  resendReservationNotification,
  cancelReservation,
} from "../../api/reservation.api";

import { Breadcrumbs } from "../../AbstractElements";
import { handleWhatsAppNotify } from "../../utils/whatsapp";
import { getTodayRange } from "../../utils/datetime";
import { getReservationMasterData } from "../../api/reservation.api";
import { hasRole } from "../../utils/auth";
import "./rsvn.css"

const ReservationMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const [schedulerView, setSchedulerView] = useState(() => {
    const resource = params.get("resource");
    return resource === "inventory" ? "inventory" : "lots";
  });
  const [calendarView, setCalendarView] = useState(() => {
    const mode = params.get("mode");
    return mode === "monthly" ? "monthly" : "timeline";
  });
  const [expandedLocations, setExpandedLocations] = useState({});
  const [filters, setFilters] = useState({
    range: "all",
    type: "",
  });
  const canManageReservations = hasRole("superadmin", "finance");

  const defaultQuery = {
    sortField: "startDate",
    sortOrder: "desc",
    name: "",
    bookingRef: "",
    lotId: "",
    status: "",
    type: "",
    date: "",
  };

  const [query, setQuery] = useState(defaultQuery);
  const [draftQuery, setDraftQuery] = useState(defaultQuery);
  const [lots, setLots] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const week = new URLSearchParams(window.location.search).get("week");
    if (!week) {
      return new Date();
    }
    const parsed = new Date(week);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  });
  const [loading, setLoading] = useState(false);
  const visibleDays =
    calendarView === "timeline"
      ? 14
      : new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();

  const LEFT_COLUMN_WIDTH = 150;
  const MIN_DAY_WIDTH = 90;

  const dayWidth = Math.max(
    MIN_DAY_WIDTH,
    (window.innerWidth - LEFT_COLUMN_WIDTH - 60) / visibleDays
  );

  const gridWidth = dayWidth * visibleDays;

  const timelineDates = useMemo(() => {
    return Array.from({ length: visibleDays }, (_, i) => {
        const d = new Date(startDate);
        d.setHours(0,0,0,0);
        d.setDate(d.getDate() + i);
        return d;
    });
  }, [startDate]);

  const EVENT_COLORS = {
    reservation: "#89b324",
    holiday: "#8B5CF6",
    maintenance: "#d4a437",
    event: "#7da8c7",
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const updateUrlParams = (
    date,
    resource = schedulerView,
    mode = calendarView
  ) => {
    const params = new URLSearchParams(location.search);

    params.set("week", formatDate(date));
    params.set("resource", resource);
    params.set("mode", mode);

    setSearchParams(params, { replace: true });
  };

  const fetchReservationsData = async () => {
    try {
      setLoading(true);

      const rangeStart = new Date(startDate);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(startDate);
      rangeEnd.setHours(0, 0, 0, 0);
      if (calendarView === "timeline") {
        rangeEnd.setDate(rangeEnd.getDate() + visibleDays - 1);
      } else {
        rangeStart.setDate(1);
        rangeEnd.setMonth(rangeEnd.getMonth() + 1);
        rangeEnd.setDate(0);
      }
      const res = await getReservations({
        view: "scheduler",
        startDate: formatDate(rangeStart),
        endDate: formatDate(rangeEnd),
        sortField: query.sortField,
        sortOrder: query.sortOrder,
        name: query.name,
        bookingRef: query.bookingRef,
        lotId: query.lotId,
        status: query.status,
        type: query.type,
      });
      setData(res.data || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hydrated) return;
    fetchReservationsData();
  }, [query, startDate, hydrated]);

  useEffect(() => {
    const week = searchParams.get("week");
    if (!week) return;

    const parsed = new Date(week);
    if (Number.isNaN(parsed.getTime())) return;

    if (formatDate(parsed) !== formatDate(startDate)) {
      setStartDate(parsed);
    }

    const resource = searchParams.get("resource");
    if (resource === "lots" || resource === "inventory") {
      setSchedulerView(resource);
    }

    const mode = searchParams.get("mode");

    if (mode === "timeline" || mode === "monthly") {
      setCalendarView(mode);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const res = await getReservationMasterData();

        setLots(res?.lots || []);
        setAddOns(res?.addOns || []);
      } catch (err) {
        toast.error(err.message);
      }
    };

    fetchMasterData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFromUrl = params.get("status");

    const initial = {
      ...defaultQuery,
      status: statusFromUrl || "",
    };

    const resetQuery = {
      ...defaultQuery,
      status: query.status,
    };

    setDraftQuery(resetQuery);
    setQuery(resetQuery);
    setHydrated(true);
  }, [location.search]);

  const lotSchedulerRows = useMemo(() => {
    return lots.map((lot) => {
      const reservations = [];

      data.forEach((reservation) => {
        (reservation.orderItems || []).forEach((item) => {
          if (
            item.itemType === "lot" &&
            Number(item.itemId) === Number(lot.id)
          ) {
            reservations.push(reservation);
          }
        });
      });

      return { ...lot, reservations };
    });
  }, [lots, data]);

  const inventorySchedulerRows = useMemo(() => {
    return addOns
      .map(addOn => {
        const reservations = [];

        data.forEach(reservation => {
          (reservation.orderItems || []).forEach(item => {
            if (item.itemType === "add_on" && Number(item.itemId) === Number(addOn.id)) {
              reservations.push(reservation);
            }
          });
        });

        return { ...addOn, reservations };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [addOns, data]);

  const groupedLotSchedulerRows = useMemo(() => {
    const groups = {};

    lotSchedulerRows.forEach(lot => {
      const location = lot.location?.trim() || "Others";
      if (!groups[location]) groups[location] = [];
      groups[location].push(lot);
    });

    return Object.entries(groups).map(([location, lots]) => ({ location, lots }));
  }, [lotSchedulerRows]);

  const toggleLocation = location => {
    setExpandedLocations(prev => ({
      ...prev,
      [location]: prev[location] === false,
    }));
  };

  const DAY = 1000 * 60 * 60 * 24;
  const getReservationBar = (reservation) => {
    const start = new Date(reservation.startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(reservation.endDate);
    end.setHours(0, 0, 0, 0);

    const firstVisible = new Date(timelineDates[0]);
    firstVisible.setHours(0, 0, 0, 0);

    const lastVisible = new Date(timelineDates.at(-1));
    lastVisible.setHours(0, 0, 0, 0);

    if (end < firstVisible || start > lastVisible) return null;

    const clippedStart = start < firstVisible ? firstVisible : start;
    const clippedEnd = end > lastVisible ? lastVisible : end;

    return {
      startIndex: Math.floor(
        (clippedStart - firstVisible) / DAY
      ),
      span:
        Math.floor(
          (clippedEnd - clippedStart) / DAY
        ) + 1,
    };
  };

  const handleView = (row) => {
    navigate(`/reservations/view/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/reservations/edit/${row.id}`);
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(t("confirm delete"));
    if (!confirmed) return;

    try {
      setDeletingId(row.id);
      await deleteReservation(row.id);
      toast.success(t("deleted successfully"));
      fetchReservationsData({ page: 1 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreate = () => {
    navigate("/reservations/create");
  };

  const getEventColor = (reservation) => {
    switch (reservation.type) {
      case "holiday":
        return EVENT_COLORS.holiday;

      case "maintenance":
        return EVENT_COLORS.maintenance;

      case "event":
      case "special_event":
        return EVENT_COLORS.event;

      default:
        return EVENT_COLORS.reservation;
    }
  };

  const getEventTitle = (event) => {
    switch (event.type) {
      case "booking":
        return `${event.bookingRef ?? event.name} (${titleCase(event.status)})`;

      case "holiday":
        return "Holiday";

      case "maintenance":
        return "Maintenance";

      case "special_event":
        return "Event";

      default:
        return event.bookingRef || "-";
    }
  };

  const handleCancel = async (reservation) => {
    const confirmed = window.confirm(
      t("Are you sure you want to cancel this reservation?")
    );

    if (!confirmed) return;

    try {
      await cancelReservation(reservation.id);

      toast.success(t("Reservation cancelled successfully."));

      fetchReservationsData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteEvent = (reservation) => {
    handleDelete(reservation);
  };

  const getReservationActions = (reservation) => {
    const actions = [
      {
        label: titleCase(t("details")),
        action: () => handleView(reservation),
      },
    ];

    if (!canManageReservations) {
      return actions;
    }

    actions.push({
      label: titleCase(t("edit")),
      action: () => handleEdit(reservation),
    });

    switch (reservation.type) {
      case "holiday":
        actions.push({
          dividerBefore: true,
          label: titleCase(t("delete")),
          action: () => handleDeleteEvent(reservation),
          color: "danger",
        });
        break;

      case "maintenance":
      case "special_event":
        actions.push({
          dividerBefore: true,
          label: titleCase(t("delete")),
          action: () => handleDeleteEvent(reservation),
          color: "danger",
        });
        break;

      default:
        if (reservation.status !== "cancelled") {
          actions.push({
            dividerBefore: true,
            label: titleCase(t("cancel")),
            action: () => handleCancel(reservation),
            color: "danger",
          });
        }
        break;
    }

    return actions;
  };

  const getEventClass = (reservation) => {
    switch (reservation.type) {
      case "holiday":
        return "scheduler-badge-holiday";

      case "maintenance":
        return "scheduler-badge-maintenance";

      case "event":
      case "special_event":
        return "scheduler-badge-event";

      default:
        return "scheduler-badge-reservation";
    }
  };

  const formatMonthDate = (date) =>
    new Date(date).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
    });

  const schedulerRows = schedulerView === "lots" ? lotSchedulerRows : inventorySchedulerRows;
  const monthlySchedulerRows = useMemo(() => {
    return schedulerRows.map((resource) => ({
      ...resource,
      reservations: [...resource.reservations].sort(
        (a, b) =>
          new Date(a.startDate) - new Date(b.startDate)
      ),
    }));
  }, [schedulerRows]);

  const changePeriod = (direction) => {
    const d = new Date(startDate);

    if (calendarView === "timeline") {
      d.setDate(d.getDate() + direction * 7);
    } else {
      d.setMonth(d.getMonth() + direction);
      d.setDate(1);
    }

    setStartDate(d);
    updateUrlParams(d);
  };

  const goToCurrentPeriod = () => {
    const d = new Date();

    if (calendarView === "monthly") {
      d.setDate(1);
    }

    setStartDate(d);
    updateUrlParams(d);
  };

  const getReservationStatusColor = (status) => ({
    confirmed: "success",
    pending: "warning",
    cancelled: "danger",
  }[status] ?? "secondary");

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reservations"))}
        title={titleCase(t("reservation management"))}
        mainTitle={titleCase(t("reservation management"))}
      />

      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="mb-3">
              <CardHeader>
                <div className="d-flex justify-content-end align-items-center w-100 gap-2">
                  <Button color="light" onClick={() => setShowFilters(!showFilters)}>
                    {showFilters
                      ? t("hide filters").toUpperCase()
                      : t("show filters").toUpperCase()}
                  </Button>

                  {canManageReservations && (
                    <Button color="success" onClick={handleCreate}>
                      {t("create").toUpperCase()}
                    </Button>
                  )}
                </div>
              </CardHeader>
            </Card>
            
            <Card className="mb-3">
              <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <UncontrolledButtonDropdown>
                    <DropdownToggle outline color="primary" caret>
                      {schedulerView === "lots"
                        ? titleCase(t("lots"))
                        : titleCase(t("inventory"))}
                    </DropdownToggle>

                    <DropdownMenu>
                      <DropdownItem
                        active={schedulerView === "lots"}
                        onClick={() => {
                          setSchedulerView("lots");
                          updateUrlParams(startDate, "lots", calendarView);
                        }}
                      >
                        {titleCase(t("lots"))}
                      </DropdownItem>

                      <DropdownItem
                        active={schedulerView === "inventory"}
                        onClick={() => {
                          setSchedulerView("inventory");
                          updateUrlParams(startDate, "inventory", calendarView);
                        }}
                      >
                        {titleCase(t("inventory"))}
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>
                  <div className="vr mx-2" />
                  <UncontrolledButtonDropdown>
                    <DropdownToggle outline color="primary" caret>
                      {calendarView === "timeline"
                        ? titleCase(t("timeline"))
                        : titleCase("monthly")}
                    </DropdownToggle>

                    <DropdownMenu>
                      <DropdownItem
                        active={calendarView === "timeline"}
                        onClick={() => {
                          setCalendarView("timeline");
                          updateUrlParams(startDate, schedulerView, "timeline");
                        }}
                      >
                        {titleCase(t("timeline"))}
                      </DropdownItem>

                      <DropdownItem
                        active={calendarView === "monthly"}
                        onClick={() => {
                          const d = new Date();
                          d.setDate(1);

                          setCalendarView("monthly");
                          setStartDate(d);

                          updateUrlParams(d, schedulerView, "monthly");
                        }}
                      >
                        {titleCase("monthly")}
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>
                  <div className="vr mx-2" />
                  <div className="btn-group custom-btn-group">
                    <Button outline onClick={() => changePeriod(-1)}>
                      {titleCase(
                        t(calendarView === "timeline" ? "previous week" : "previous month")
                      )}
                    </Button>

                    <Button outline onClick={goToCurrentPeriod}>
                      {titleCase(
                        t(calendarView === "timeline" ? "today" : "this month")
                      )}
                    </Button>

                    <Button outline onClick={() => changePeriod(1)}>
                      {titleCase(
                        t(calendarView === "timeline" ? "next week" : "next month")
                      )}
                    </Button>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Badge
                    pill
                    className="scheduler-badge-reservation"
                    style={{ padding: "8px 12px" }}
                  >
                    Reservation
                  </Badge>

                  <Badge
                    pill
                    className="scheduler-badge-holiday"
                    style={{ padding: "8px 12px" }}
                  >
                    Holiday
                  </Badge>

                  <Badge
                    pill
                    className="scheduler-badge-maintenance"
                    style={{ padding: "8px 12px" }}
                  >
                    Maintenance
                  </Badge>

                  <Badge
                    pill
                    className="scheduler-badge-event"
                    style={{ padding: "8px 12px" }}
                  >
                    Event
                  </Badge>
                </div>
              </CardBody>
            </Card>
            
            {showFilters && (
              <Card className="mb-3">
                <CardBody>
                  <Row className="g-3">
                    <Col md="4">
                      <label className="form-label">
                        {titleCase(t("guest name"))}
                      </label>

                      <input
                        className="form-control"
                        value={draftQuery.name}
                        onChange={(e) =>
                          setDraftQuery((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder={t("Search guest name")}
                      />
                    </Col>

                    <Col md="4">
                      <label className="form-label">
                        {titleCase(t("booking reference"))}
                      </label>

                      <input
                        className="form-control"
                        value={draftQuery.bookingRef}
                        onChange={(e) =>
                          setDraftQuery((prev) => ({
                            ...prev,
                            bookingRef: e.target.value,
                          }))
                        }
                        placeholder={t("Search booking reference")}
                      />
                    </Col>

                    <Col md="4">
                      <label className="form-label">
                        {titleCase(t("reservation type"))}
                      </label>

                      <select
                        className="form-select"
                        value={draftQuery.type}
                        onChange={(e) =>
                          setDraftQuery((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                      >
                        <option value="">{titleCase(t("All"))}</option>
                        <option value="booking">{titleCase(t("Booking"))}</option>
                        <option value="holiday">{titleCase(t("Holiday"))}</option>
                        <option value="maintenance">{titleCase(t("Maintenance"))}</option>
                        <option value="special_event">{titleCase(t("Event"))}</option>
                      </select>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                      color="light"
                      onClick={() => {
                        setDraftQuery(defaultQuery);
                        setQuery(defaultQuery);
                      }}
                    >
                      {titleCase(t("Reset"))}
                    </Button>

                    <Button
                      color="primary"
                      onClick={() => setQuery(draftQuery)}
                    >
                      {titleCase(t("Apply"))}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardBody className="p-0">
                {calendarView === "timeline" ? (
                  <div className="scheduler-wrapper">
                    <div
                      className="scheduler-header"
                      style={{
                        width: LEFT_COLUMN_WIDTH + gridWidth,
                        gridTemplateColumns: `150px repeat(${visibleDays}, ${dayWidth}px)`,
                      }}
                    >
                      <div className="scheduler-lot-header">
                        {schedulerView === "lots" ? titleCase(t("lot")) : titleCase(t("inventory"))}
                      </div>
                      {timelineDates.map((date) => (
                        <div key={date.toISOString()} className="scheduler-day">
                          <div>
                            {date.toLocaleDateString(undefined, {
                              day: "2-digit",
                              month: "short",
                            })}
                          </div>
                          <small>
                            {date.toLocaleDateString(undefined, {
                              weekday: "short",
                            })}
                          </small>
                        </div>
                      ))}
                    </div>
                    <div className="scheduler-table">
                      {schedulerView === "lots" ? groupedLotSchedulerRows.map((group) => (
                        <Fragment key={group.location}>
                          <div
                            className="scheduler-location-row"
                            style={{ width: LEFT_COLUMN_WIDTH + gridWidth }}
                            onClick={() => toggleLocation(group.location)}
                          >
                            <div className="scheduler-location-label">
                              {!expandedLocations[group.location] ? "▶" : "▼"} {group.location}
                            </div>
                          </div>
                          {expandedLocations[group.location] !== false && group.lots.map((resource) => (
                            <div className="scheduler-row" key={resource.id}>
                              <div className="scheduler-lot">
                                {resource.name}  
                              </div>

                              <div
                                className="scheduler-grid"
                                style={{
                                  width: gridWidth,
                                }}
                              >
                                <div
                                  className="scheduler-background"
                                  style={{
                                    gridTemplateColumns: `repeat(${visibleDays}, ${dayWidth}px)`,
                                  }}
                                >
                                  {timelineDates.map((date) => (
                                    <div
                                      key={date.toISOString()}
                                      className="scheduler-cell"
                                    />
                                  ))}
                                </div>

                                {resource.reservations.map((reservation) => {
                                  const bar = getReservationBar(reservation);

                                  if (!bar) return null;

                                  return (
                                    <div
                                      key={reservation.id}
                                      className="scheduler-bar-container"
                                      style={{
                                        left: `${bar.startIndex * dayWidth}px`,
                                        width: `${bar.span * dayWidth}px`,
                                      }}
                                    >
                                      <UncontrolledDropdown className="w-100 h-100">
                                        <DropdownToggle
                                          tag="div"
                                          caret={false}
                                          className={`scheduler-bar ${getEventClass(reservation)}`}
                                        >
                                          {getEventTitle(reservation)}
                                        </DropdownToggle>

                                        <DropdownMenu end>
                                          {getReservationActions(reservation).map((item) => (
                                            <Fragment key={item.label}>
                                              {item.dividerBefore && (
                                                <DropdownItem divider />
                                              )}

                                              <DropdownItem
                                                className={
                                                  item.color === "danger"
                                                    ? "text-danger"
                                                    : ""
                                                }
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  item.action();
                                                }}
                                              >
                                                  {item.label}
                                              </DropdownItem>
                                            </Fragment>
                                          ))}
                                        </DropdownMenu>
                                      </UncontrolledDropdown>
                                    </div>
                                  );
                                })}
                            </div>
                            </div>
                          ))}
                          </Fragment>
                        )) : schedulerRows.map((resource) => (
                          <div className="scheduler-row" key={resource.id}>
                            <div className="scheduler-lot">
                              {resource.name}  
                            </div>

                            <div
                              className="scheduler-grid"
                              style={{
                                width: gridWidth,
                              }}
                            >
                              <div
                                className="scheduler-background"
                                style={{
                                  gridTemplateColumns: `repeat(${visibleDays}, ${dayWidth}px)`,
                                }}
                              >
                                {timelineDates.map((date) => (
                                  <div
                                    key={date.toISOString()}
                                    className="scheduler-cell"
                                  />
                                ))}
                              </div>

                              {resource.reservations.map((reservation) => {
                                const bar = getReservationBar(reservation);

                                if (!bar) return null;

                                return (
                                  <div
                                    key={reservation.id}
                                    className="scheduler-bar-container"
                                    style={{
                                      left: `${bar.startIndex * dayWidth}px`,
                                      width: `${bar.span * dayWidth}px`,
                                    }}
                                  >
                                    <UncontrolledDropdown className="w-100 h-100">
                                      <DropdownToggle
                                        tag="div"
                                        caret={false}
                                        className={`scheduler-bar ${getEventClass(reservation)}`}
                                      >
                                        {getEventTitle(reservation)}
                                      </DropdownToggle>

                                      <DropdownMenu end>
                                        {getReservationActions(reservation).map((item) => (
                                          <Fragment key={item.label}>
                                            {item.dividerBefore && (
                                              <DropdownItem divider />
                                            )}

                                            <DropdownItem
                                              className={
                                                item.color === "danger"
                                                  ? "text-danger"
                                                  : ""
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                item.action();
                                              }}
                                            >
                                                {item.label}
                                            </DropdownItem>
                                          </Fragment>
                                        ))}
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </div>
                                );
                              })}
                          </div>
                          </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 150 }}>
                            {titleCase(t(schedulerView === "lots" ? "lot" : "inventory"))}
                          </th>
                          <th>
                            {startDate.toLocaleDateString(undefined, {
                              month: "long",
                              year: "numeric",
                            })}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {monthlySchedulerRows.map((resource) => (
                          <tr key={resource.id}>
                            <td className="fw-semibold">{resource.name}</td>

                            <td>
                              {resource.reservations.length === 0 ? (
                                <span className="text-muted">-</span>
                              ) : (
                                resource.reservations.map((reservation) => (
                                  <UncontrolledDropdown key={reservation.id} className="monthly-reservation">
                                    <DropdownToggle
                                      tag="div"
                                      caret={false}
                                      className={`monthly-reservation-item ${getEventClass(reservation)}`}
                                    >
                                      <div className="d-flex justify-content-between align-items-center w-100">
                                        <div>
                                          <strong>
                                            {formatMonthDate(reservation.startDate)} - {formatMonthDate(reservation.endDate)}
                                          </strong>
                                          {" • "}
                                          {reservation.bookingRef || reservation.name}
                                          {` (${titleCase(t(reservation.status))})`}
                                        </div>
                                      </div>
                                    </DropdownToggle>

                                    <DropdownMenu end>
                                      {getReservationActions(reservation).map((item) => (
                                        <Fragment key={item.label}>
                                          {item.dividerBefore && <DropdownItem divider />}
                                          <DropdownItem
                                            className={item.color === "danger" ? "text-danger" : undefined}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              item.action();
                                            }}
                                          >
                                            {item.label}
                                          </DropdownItem>
                                        </Fragment>
                                      ))}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                ))
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReservationMain;